import { useEffect, useRef } from 'react'
import { useAuthStore } from '@/app/store/auth'
import { getSessionConfig } from '@/lib/session-config'

/**
 * Hook to handle session timeout
 * Checks for session expiry on mount and sets up periodic checks
 * Also handles session timeout warnings
 */
export function useSessionTimeout() {
    const {
        checkSessionTimeout,
        checkSessionTimeoutWarning,
        isAuthenticated
    } = useAuthStore()
    const intervalRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        // Only run session timeout checks if user is authenticated
        if (!isAuthenticated) {
            return
        }

        // Check session timeout immediately on mount (handles page refresh)
        const isSessionValid = checkSessionTimeout()

        if (!isSessionValid) {
            console.log('Session expired on page load - redirecting to login')
            return
        }

        // Set up periodic session timeout checks
        const config = getSessionConfig()
        intervalRef.current = setInterval(() => {
            // First check for warnings
            const _warningShown = checkSessionTimeoutWarning()

            // Then check for actual timeout
            const isValid = checkSessionTimeout()
            if (!isValid) {
                console.log('Session expired during periodic check - redirecting to login')
                clearInterval(intervalRef.current!)
            }
        }, config.checkIntervalMs)

        // Cleanup interval on unmount
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
            }
        }
    }, [isAuthenticated, checkSessionTimeout, checkSessionTimeoutWarning])

    // Also check on visibility change (when user switches tabs)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && isAuthenticated) {
                // Check for warnings first
                checkSessionTimeoutWarning()

                // Then check for actual timeout
                const isValid = checkSessionTimeout()
                if (!isValid) {
                    console.log('Session expired on tab focus - redirecting to login')
                }
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [isAuthenticated, checkSessionTimeout, checkSessionTimeoutWarning])
}

/**
 * Hook to handle user activity and extend session
 * Resets the last activity timestamp when user is active
 * Also hides session timeout warnings when user is active
 */
export function useUserActivity() {
    // Use a selector to only subscribe to these two stable action references,
    // NOT the entire store.  Without the selector, every single Zustand state
    // change (including sessionTimeoutWarning) caused this hook's host
    // component (ProtectedRoute → AppShell) to re-render on every scroll tick.
    const updateTokenExpiry = useAuthStore((s) => s.updateTokenExpiry)
    const hideSessionTimeoutWarning = useAuthStore((s) => s.hideSessionTimeoutWarning)
    const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        const handleUserActivity = () => {
            // Hide any active session timeout warning when user is active
            hideSessionTimeoutWarning()

            // Clear existing timeout
            if (activityTimeoutRef.current) {
                clearTimeout(activityTimeoutRef.current)
            }

            // Set new timeout to reset last activity
            const config = getSessionConfig()
            activityTimeoutRef.current = setTimeout(() => {
                // Update token expiry to extend session
                updateTokenExpiry(config.sessionExtensionSeconds)
            }, config.activityTimeoutMs)
        }

        // Listen for user activity events
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']

        events.forEach(event => {
            document.addEventListener(event, handleUserActivity, true)
        })

        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleUserActivity, true)
            })

            if (activityTimeoutRef.current) {
                clearTimeout(activityTimeoutRef.current)
            }
        }
    }, [updateTokenExpiry, hideSessionTimeoutWarning])
}
